import type { Control, FieldErrors, UseFormRegister } from "react-hook-form";
import { Controller, useWatch } from "react-hook-form";

import { FormCheckbox, FormCheckboxGroup } from "@/components/forms/FormCheckbox";
import { FormFieldShell } from "@/components/forms/FormField";
import { FormInput } from "@/components/forms/FormInput";
import { Heading } from "@/components/typography";
import { Card, CardContent } from "@/components/ui/card";
import { Field, FieldGroup, FieldLabel } from "@/components/ui/field";
import { Switch } from "@/components/ui/switch";
import ManagerSelect from "../../../components/ManagerSelect";
import MultiSelect from "../../../components/MultiSelect";
import { asIdList } from "../../../shared/asIdList";
import { leadSourceList } from "../../../shared/grailConstants";
import type { User } from "../../../shared/types";
import type { CampaignFormValues } from "../campaign";

type ExtrasSectionProps = {
    control: Control<CampaignFormValues>;
    errors: FieldErrors<CampaignFormValues>;
    register: UseFormRegister<CampaignFormValues>;
    showNotesAndRates: boolean;
    onShowNotesAndRatesChange: (value: boolean) => void;
    sendBookerPitchDigest: boolean;
    onSendBookerPitchDigestChange: (value: boolean) => void;
    isBookerPitchDigestShown: boolean;
    reviewer: boolean;
    isSalesOrAdmin: boolean;
    salesTeam: User[];
};

/** Boost, notes and rates, and the sales/admin fields. Watches lead source to require the CRM id and sales-team member. */
export function ExtrasSection({
    control,
    errors,
    register,
    showNotesAndRates,
    onShowNotesAndRatesChange,
    sendBookerPitchDigest,
    onSendBookerPitchDigestChange,
    isBookerPitchDigestShown,
    reviewer,
    isSalesOrAdmin,
    salesTeam,
}: ExtrasSectionProps) {
    const leadSource = useWatch({ control, name: "leadSource" });
    const leadSourceIdRequired = Boolean(leadSource?.includes("CRM Lead"));
    const salesTeamRequired = Boolean(leadSource?.includes("Sales Team"));

    return (
        <section id="extras" className="scroll-mt-16">
            <Heading type="h4" className="mb-4 font-bold">
                Extras
            </Heading>
            <Card size="sm">
                <CardContent>
                    <FieldGroup className="gap-6">
                        <Controller
                            name="boostInterest"
                            control={control}
                            render={({ field }) => (
                                <FormCheckboxGroup label="Are you interested in Boosting posts for this campaign?">
                                    <FormCheckbox
                                        label="Yes, please reach out to me about Boosting"
                                        checked={Boolean(field.value)}
                                        onChange={field.onChange}
                                        ref={field.ref}
                                        onBlur={field.onBlur}
                                    />
                                </FormCheckboxGroup>
                            )}
                        />

                        <Field orientation="horizontal">
                            <Switch
                                checked={showNotesAndRates}
                                disabled={!reviewer}
                                onCheckedChange={(checked) =>
                                    onShowNotesAndRatesChange(checked === true)
                                }
                            />
                            <FieldLabel>
                                Show notes and rates to other managers
                            </FieldLabel>
                        </Field>

                        {isBookerPitchDigestShown ? (
                            <Field orientation="horizontal">
                                <Switch
                                    checked={sendBookerPitchDigest}
                                    disabled={!reviewer}
                                    onCheckedChange={(checked) =>
                                        onSendBookerPitchDigestChange(checked === true)
                                    }
                                />
                                <FieldLabel>
                                    Email the booker a daily pitch digest
                                </FieldLabel>
                            </Field>
                        ) : null}

                        {isSalesOrAdmin ? (
                            <>
                                <Field className="gap-1">
                                    <FieldLabel>Lead Source</FieldLabel>
                                    <Controller
                                        name="leadSource"
                                        control={control}
                                        render={({ field }) => (
                                            <MultiSelect
                                                options={leadSourceList}
                                                onChange={field.onChange}
                                                value={field.value || []}
                                                isErrored={Boolean(errors.leadSource)}
                                            />
                                        )}
                                    />
                                </Field>
                                {leadSourceIdRequired ? (
                                    <FormInput
                                        label="Twenty CRM Lead ID"
                                        required
                                        type="text"
                                        error={
                                            errors.crmLeadId
                                                ? "Lead Source ID is required when lead sources include 'CRM Lead'."
                                                : undefined
                                        }
                                        {...register("crmLeadId", {
                                            required: leadSourceIdRequired,
                                        })}
                                    />
                                ) : null}
                                {salesTeamRequired ? (
                                    <Controller
                                        name="salesTeamId"
                                        control={control}
                                        rules={{ validate: (value) => asIdList(value).length > 0 }}
                                        render={({ field }) => (
                                            <FormFieldShell
                                                label="Sales Team Member"
                                                required
                                                htmlFor="salesTeamId"
                                                invalid={Boolean(errors.salesTeamId)}
                                                error={
                                                    errors.salesTeamId
                                                        ? "Sales Team Member is required."
                                                        : undefined
                                                }
                                            >
                                                {() => (
                                                    <ManagerSelect
                                                        managers={salesTeam}
                                                        value={asIdList(field.value)}
                                                        onChange={(_event: unknown, next: string[]) => {
                                                            field.onChange(next);
                                                        }}
                                                    />
                                                )}
                                            </FormFieldShell>
                                        )}
                                    />
                                ) : null}
                            </>
                        ) : null}
                    </FieldGroup>
                </CardContent>
            </Card>
        </section>
    );
}
